import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { debugValue } from "@/env/utils";
import { DEFAULT_LANGUAGE, i18n } from "@/i18n";
import { Capacitor } from "@/lib/capacitor";
import { Device } from "@/lib/capacitor/device";
import { createSelectors } from "@/lib/store/utils/create-selectors";
import { CapacitorStorage } from "@/lib/store/utils/storage";

export type PreferencesState = {
  language: "system" | Language;
  reducedHaptics: boolean;
};

export type PreferencesActions = {
  setLanguage: (language: PreferencesState["language"]) => void;
  setReducedHaptics: (reducedHaptics: PreferencesState["reducedHaptics"]) => void;
};

export const usePreferencesStore = create<PreferencesState & PreferencesActions>()(
  devtools(
    persist(
      (set) => ({
        // State
        language: "system",
        reducedHaptics: false,

        // Actions
        setLanguage: (language) => {
          set({ language });
          enforceLanguage(language);
        },
        setReducedHaptics: (reducedHaptics) => {
          set({ reducedHaptics });
        },
      }),
      {
        name: "preferences",
        storage: createJSONStorage(() => CapacitorStorage),
        onRehydrateStorage: () => (state) => {
          if (state?.language) {
            state.setLanguage(state?.language);
          }
          if (state?.reducedHaptics) {
            state.setReducedHaptics(state.reducedHaptics);
          }
        },
      },
    ),
    {
      enabled: debugValue("store") && Capacitor.Platform === "web",
    },
  ),
);

export const useSelectorPreferencesStore = createSelectors(usePreferencesStore);

export async function getComputedLanguage(
  language: PreferencesState["language"],
): Promise<Language> {
  switch (language) {
    case "system":
      return Device.getLanguageCode().then(({ value }) =>
        i18n.languages.includes(value) ? (value as Language) : DEFAULT_LANGUAGE,
      );
    default:
      return language;
  }
}

export async function enforceLanguage(language: PreferencesState["language"]) {
  switch (language) {
    case "system":
      Device.getLanguageCode().then(({ value }) => {
        enforceLanguage(i18n.languages.includes(value) ? (value as Language) : DEFAULT_LANGUAGE);
      });
      break;
    default:
      i18n.changeLanguage(language);
      break;
  }
}
